import { useEffect, useState } from 'react';
import { Button, notification } from 'antd';
import { ReactComponent as Reply } from 'Assets/links/reply.svg';
import { useAppDispatch } from 'Redux/store';
import { refuseOrder } from 'Redux/Order/reducer';
import { getOrder } from 'Redux/Order/reducer';
import { useSelector } from 'react-redux';
import { selectCurrentOrder, selectOrderLoading } from 'Redux/Order/slice';
import { getMailLetterTimeStamp } from 'Shared/Utils';
import { AssigningBidModal } from 'Components/AssigningBidModal/AssigningBidModal';
import ContentWithBreaks from 'Components/UI/ContentWithBreaks';
import styles from './Email.module.scss';

interface EmailParams {
  brokerPrice?: number;
  driverPrice?: number;
}

export const Email: React.FC<EmailParams> = ({ brokerPrice, driverPrice }) => {
  const dispatch = useAppDispatch();
  const currentBid = useSelector(selectCurrentOrder);
  const isLoading = useSelector(selectOrderLoading);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [notificationAPI, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (
      currentBid &&
      !currentBid.order_status.toLowerCase().includes('await')
    ) {
      setButtonsDisabled(true);
    }
  }, [currentBid]);

  const refuseHandler = () => {
    if (currentBid?.id) {
      dispatch(refuseOrder({ order_id: currentBid.id })).then((action) => {
        if (action.payload) {
          notificationAPI.success({
            message: 'Bid has been refused',
            duration: 2,
          });
          setButtonsDisabled(true);
          dispatch(getOrder(currentBid.id));
        } else {
          notificationAPI.error({
            message: "Internal error, bid wasn't refused",
            duration: 4,
          });
        }
      });
    }
  };

  const assignHandler = () => {
    setModalIsVisible(true);
  };

  return (
    <div className={styles.email}>
      {contextHolder}
      <div className={styles.email__header}>
        <h3 className={styles.email__header__title}>Email</h3>
        {currentBid && (
          <div className={styles.email__header__controls}>
            <Button
              className={styles.email__header__control}
              type="primary"
              danger
              onClick={refuseHandler}
              disabled={buttonsDisabled}
              title={
                buttonsDisabled
                  ? `Bid was ${
                      currentBid?.order_status.toLowerCase() === 'refused'
                        ? 'refused'
                        : 'assigned'
                    }`
                  : 'Press to refuse the bid'
              }
            >
              Refuse
            </Button>

            <Button
              className={styles.email__header__control}
              type="primary"
              onClick={assignHandler}
              disabled={buttonsDisabled}
              title={
                buttonsDisabled
                  ? `Bid was ${
                      currentBid?.order_status.toLowerCase() === 'refused'
                        ? 'refused'
                        : 'assigned'
                    }`
                  : 'Press to assign the bid'
              }
            >
              Assign shipment
            </Button>
          </div>
        )}
      </div>

      <div className={styles.email__body}>
        {currentBid && currentBid.letter && (
          <div className={styles.email__senderInfo}>
            <span className={styles.email__senderInfo__emailAddress}>
              {currentBid?.email}
            </span>
            <span className={styles.email__senderInfo__passedTime}>
              {getMailLetterTimeStamp(currentBid.letter?.created_at)}
            </span>
            <a
              className={styles.email__senderInfo__link}
              href={`mailto:${currentBid?.email}`}
              target="_blank"
              rel="noreferrer"
            >
              <Reply title="Reply to the broker via the email address." />
            </a>
          </div>
        )}

        <div className={styles.email__content}>
          {currentBid && currentBid.letter ? (
            <ContentWithBreaks content={currentBid.letter?.comment} />
          ) : (
            `Email letter ${isLoading ? 'is loading...' : 'not found'}`
          )}
        </div>
      </div>

      <AssigningBidModal
        visible={modalIsVisible}
        onCancel={() => setModalIsVisible(false)}
        brokerPrice={brokerPrice}
        driverPrice={driverPrice}
      />
    </div>
  );
};
